import {useMemo} from 'react';

import {iriToId} from 'shared/api/iriHelper';

import {additionalEmailAPI} from '../api/additionalEmailAPI';
import {maxChatAPI} from '../api/maxChatAPI';
import {telegramChatAPI} from '../api/telegramChatAPI';
import {getAdditionalEmailLabel, getMessengerChatLabel} from '../lib/notifyChannelLabel';

import {IAdditionalEmail, IMessengerChat, INotifyChannel} from './INotifyChannel';

export interface INotifyChannelName {
    id: string,
    name: string,
}

export interface INotifyChannelNames {
    additionalEmails: (_iris: string[]) => INotifyChannelName[],
    telegramChats: (_iris: string[]) => INotifyChannelName[],
    maxChats: (_iris: string[]) => INotifyChannelName[],
}

const namesResolver = <T extends INotifyChannel>(items: T[] | undefined, getLabel: (_item: T) => string) =>
    (iris: string[]): INotifyChannelName[] => iris.reduce<INotifyChannelName[]>((names, iri) => {
        const id = iriToId(iri);
        const item = items?.find(channel => `${channel.id}` === id);

        if (item) {
            names.push({id: iri, name: getLabel(item)});
        }

        return names;
    }, []);

export const useNotifyChannelNames = (): INotifyChannelNames => {
    const {data: additionalEmails} = additionalEmailAPI.useGetListQuery(undefined);
    const {data: telegramChats} = telegramChatAPI.useGetListQuery(undefined);
    const {data: maxChats} = maxChatAPI.useGetListQuery(undefined);

    return useMemo(() => ({
        additionalEmails: namesResolver<IAdditionalEmail>(additionalEmails, getAdditionalEmailLabel),
        telegramChats: namesResolver<IMessengerChat>(telegramChats, getMessengerChatLabel),
        maxChats: namesResolver<IMessengerChat>(maxChats, getMessengerChatLabel),
    }), [additionalEmails, telegramChats, maxChats]);
};

export default useNotifyChannelNames;
